import {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
  isAxiosError,
} from 'axios';
import { RRWEB_ENABLED } from '@/lib/env';
import { isNullish } from '@/lib/is-nullish';
import { services } from '@/services';
import type { RrwebNetworkCall } from '../types';

type RecorderConfig = InternalAxiosRequestConfig & {
  _rrwebStartTime?: number;
  _rrwebRequestHeaders?: Record<string, string>;
};

const buffer: RrwebNetworkCall[] = [];
const attachedServices = new Map<
  AxiosInstance,
  { requestId: number; responseId: number }
>();

const extractHeaders = (
  source: Record<string, unknown> | undefined,
): Record<string, string> => {
  if (!source) return {};
  const headers: Record<string, string> = {};
  for (const [key, value] of Object.entries(source)) {
    if (!isNullish(value)) {
      headers[key] = Array.isArray(value) ? value.join(', ') : String(value);
    }
  }
  return headers;
};

const createNetworkCall = (
  config: RecorderConfig,
  response?: AxiosResponse,
): RrwebNetworkCall => {
  const timestamp = Date.now();
  return {
    method: (config.method ?? 'GET').toUpperCase(),
    url: config.url ?? '',
    status: response?.status ?? 0,
    duration: config._rrwebStartTime ? timestamp - config._rrwebStartTime : 0,
    requestBody: config.data,
    responseBody: response?.data,
    requestHeaders: config._rrwebRequestHeaders,
    responseHeaders: response
      ? extractHeaders(response.headers as Record<string, unknown>)
      : undefined,
    timestamp,
  };
};

const attachToService = (service: AxiosInstance): void => {
  const existing = attachedServices.get(service);
  if (existing) {
    service.interceptors.request.eject(existing.requestId);
    service.interceptors.response.eject(existing.responseId);
  }

  const requestId = service.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const recorderConfig = config as RecorderConfig;
      recorderConfig._rrwebStartTime = Date.now();
      recorderConfig._rrwebRequestHeaders = extractHeaders(
        config.headers as Record<string, unknown>,
      );
      return config;
    },
    (error: unknown) => Promise.reject(error),
  );

  const responseId = service.interceptors.response.use(
    (response: AxiosResponse) => {
      buffer.push(
        createNetworkCall(response.config as RecorderConfig, response),
      );
      return response;
    },
    (error: unknown) => {
      if (isAxiosError(error) && error.config) {
        buffer.push(
          createNetworkCall(
            error.config as RecorderConfig,
            error.response as AxiosResponse | undefined,
          ),
        );
      }
      return Promise.reject(error);
    },
  );

  attachedServices.set(service, { requestId, responseId });
};

export const startApiRecording = (): void => {
  if (!RRWEB_ENABLED) return;
  for (const service of services) {
    attachToService(service);
  }
};

export const reattachApiRecorder = (): void => {
  if (!RRWEB_ENABLED) return;
  for (const [service] of attachedServices) {
    attachToService(service);
  }
};

export const drainNetworkCalls = (): RrwebNetworkCall[] => {
  const snapshot = buffer.slice();
  buffer.length = 0;
  return snapshot;
};

export const peekNetworkCalls = (): RrwebNetworkCall[] => buffer.slice();

export const restoreNetworkCalls = (calls: RrwebNetworkCall[]): void => {
  buffer.push(...calls);
};
